// src/components/calendar/Calendar.tsx

import React, { useState, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    IconButton,
    Button,
    Chip,
    useTheme,
    useMediaQuery,
    Snackbar,
    Alert,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Add as AddIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Repeat as RepeatIcon,
    CardGiftcard as GiftIcon,
} from '@mui/icons-material';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    isToday,
    addMonths,
    subMonths,
    getDay,
} from 'date-fns';
import ru from 'date-fns/locale/ru';
import { useAppContext } from '../../context/AppContext';
import ModalDialog from '../common/ModalDialog';
import LessonForm, { LessonFormRef } from './LessonForm';
import { CreateLessonDto, UpdateLessonSeriesDto } from '../../types';
import { AxiosError } from 'axios';
import {
    formatLessonStartTime,
    formatLessonTimeRange,
    isLessonOnDate,
} from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import { isStudent, isTeacherOrAdmin } from '../../utils/roleUtils';
// Дни недели
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

// Функция для извлечения сообщения об ошибке
const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object' && 'isAxiosError' in error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.data) {
            const data = axiosError.response.data as any;
            if (data.error && typeof data.error === 'string') {
                return data.error;
            }
            if (data.message && typeof data.message === 'string') {
                return data.message;
            }
            if (data.errors && Array.isArray(data.errors)) {
                return data.errors.join(', ');
            }
            if (typeof data === 'string') {
                return data;
            }
        }
        if (axiosError.response?.status) {
            const status = axiosError.response.status;
            switch (status) {
                case 400:
                    return 'Неверный запрос. Проверьте введенные данные.';
                case 401:
                    return 'Необходима авторизация.';
                case 403:
                    return 'Доступ запрещен.';
                case 404:
                    return 'Запрашиваемый ресурс не найден.';
                case 409:
                    return 'Конфликт данных.';
                case 500:
                    return 'Внутренняя ошибка сервера. Попробуйте позже.';
                default:
                    return `Ошибка сервера (${status}). Попробуйте позже.`;
            }
        }
        if (axiosError.message) {
            return axiosError.message;
        }
    }
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    return 'Произошла неизвестная ошибка. Попробуйте позже.';
};

const Calendar: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const {
        lessons,
        students,
        groups,
        addLesson,
        updateLesson,
        deleteLesson,
        updateLessonSeries,
        deleteLessonSeries,
        loading
    } = useAppContext();

    const formRef = useRef<LessonFormRef>(null);
    // Текущая дата
    const [currentDate, setCurrentDate] = useState(new Date());
    // Выбранная дата - по умолчанию сегодня
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());

    // Состояния для модального окна
    const [modalOpen, setModalOpen] = useState(false);
    const [editingLessonId, setEditingLessonId] = useState<number | null>(null);
    const [editingLessonData, setEditingLessonData] = useState<CreateLessonDto | null>(null);
    const [formLoading, setFormLoading] = useState(false);

    // Состояния для диалогов выбора
    const [deleteScopeDialogOpen, setDeleteScopeDialogOpen] = useState(false);
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
    const [selectedLessonForAction, setSelectedLessonForAction] = useState<any>(null);
    const [isSeriesEditMode, setIsSeriesEditMode] = useState(false);
    const { user } = useAuth();
    const canEdit = isTeacherOrAdmin(user);
    // Состояние для уведомлений
    const [notification, setNotification] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'success',
    });

    // Переключение на предыдущий месяц
    const handlePrevMonth = () => {
        setCurrentDate(prev => subMonths(prev, 1));
    };

    // Переключение на следующий месяц
    const handleNextMonth = () => {
        setCurrentDate(prev => addMonths(prev, 1));
    };

    // Переход к сегодняшнему дню
    const handleToday = () => {
        const today = new Date();
        setCurrentDate(today);
        setSelectedDate(today);
    };

    // Выбор даты
    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
    };

    // Открыть модалку для создания урока
    const handleOpenCreate = () => {
        setEditingLessonId(null);
        setEditingLessonData(null);
        setModalOpen(true);
    };

    // Открыть модалку для редактирования урока
    const handleOpenEdit = (lesson: any) => {
        setEditingLessonId(lesson.id);
        setSelectedLessonForAction(lesson);

        const editData: CreateLessonDto = {
            studentId: lesson.studentId,
            groupId: lesson.groupId,
            date: lesson.date,
            startTime: lesson.startTime,
            endTime: lesson.endTime,
            timezone: lesson.timezone,
            topic: lesson.topic,
            recordingLink: lesson.recordingLink,
            homeworkLink: lesson.homeworkLink,
            isFree: lesson.isFree || false,
        };
        setEditingLessonData(editData);
        setIsSeriesEditMode(!!lesson.seriesId);
        setModalOpen(true);
    };

    const handleOpenDelete = (lesson: any) => {
        setSelectedLessonForAction(lesson);

        if (!lesson.seriesId) {
            setDeleteConfirmDialogOpen(true);
            return;
        }

        setDeleteScopeDialogOpen(true);
    };

    // Удалить только этот урок
    const handleDeleteSingle = async () => {
        if (!selectedLessonForAction) return;
        await handleDeleteLesson(selectedLessonForAction);
        setDeleteScopeDialogOpen(false);
        setSelectedLessonForAction(null);
    };

    // Удалить всю серию (только будущие)
    const handleDeleteSeries = async () => {
        if (!selectedLessonForAction?.seriesId) return;

        try {
            await deleteLessonSeries(selectedLessonForAction.seriesId, true);
            showNotification('Будущие уроки серии удалены', 'success');
            setDeleteScopeDialogOpen(false);
            setSelectedLessonForAction(null);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showNotification(errorMessage, 'error');
        }
    };

    // Подтверждение удаления одиночного урока
    const handleConfirmDeleteSingle = async () => {
        if (!selectedLessonForAction) return;
        await handleDeleteLesson(selectedLessonForAction);
        setDeleteConfirmDialogOpen(false);
        setSelectedLessonForAction(null);
    };

    // Закрыть модалку
    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingLessonId(null);
        setEditingLessonData(null);
        setIsSeriesEditMode(false);
        setFormLoading(false);
    };

    // Сохранение урока
    const handleSaveLesson = async (data: CreateLessonDto | UpdateLessonSeriesDto) => {
        setFormLoading(true);
        try {
            const isSeriesUpdate = 'applyTo' in data;

            if (isSeriesUpdate && selectedLessonForAction?.seriesId) {
                await updateLessonSeries(selectedLessonForAction.seriesId, data as UpdateLessonSeriesDto);
                showNotification('Серия уроков обновлена', 'success');
            } else if (editingLessonId !== null) {
                await updateLesson(editingLessonId, data as CreateLessonDto);
                showNotification('Урок успешно обновлён', 'success');
            } else {
                const result = await addLesson(data as CreateLessonDto);
                const resultData = result as any;
                if (resultData?.lessons && Array.isArray(resultData.lessons)) {
                    showNotification(`Создано уроков: ${resultData.lessons.length}`, 'success');
                } else {
                    showNotification('Урок успешно создан', 'success');
                }
            }
            handleCloseModal();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showNotification(errorMessage, 'error');
            setFormLoading(false);
        }
    };

    const handleSaveClick = () => {
        if (formRef.current) {
            formRef.current.submit();
        }
    };

    // Удаление урока
    const handleDeleteLesson = async (lesson: any) => {
        try {
            await deleteLesson(lesson.id);
            showNotification('Урок успешно удален', 'success');
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showNotification(errorMessage, 'error');
        }
    };

    const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setNotification({ open: true, message, severity });
    };

    // Получаем дни для отображения
    const calendarDays = useMemo(() => {
        const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
        const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
        return eachDayOfInterval({ start, end });
    }, [currentDate]);

    // Получаем уроки для выбранного дня (отсортированные по времени)
    const getLessonsForDay = (date: Date) => {
        return lessons
            .filter(lesson => isLessonOnDate(lesson, date))
            .sort((a, b) => {
                return formatLessonStartTime(a).localeCompare(formatLessonStartTime(b));
            });
    };

    // Получить название ученика или группы
// Получить название урока — по-разному для студента и преподавателя
    const getLessonTitle = (lesson: any) => {
        // Для студента — просто "Индивидуальный" или "Групповой"
        if (isStudent(user)) {
            return lesson.studentId ? 'Индивидуальный урок' : 'Групповой урок';
        }

        // Для TEACHER/ADMIN — как раньше
        if (lesson.studentId) {
            const student = students.find(s => s.id === lesson.studentId);
            return student ? student.fullName : 'Ученик удален';
        }
        if (lesson.groupId) {
            const group = groups.find(g => g.id === lesson.groupId);
            return group ? group.name : 'Группа удалена';
        }
        return 'Без ученика';
    };

    // Получить цвет для урока
    const getLessonColor = (lesson: any) => {
        if (lesson.studentId) {
            return theme.palette.primary.main;
        }
        if (lesson.groupId) {
            return theme.palette.secondary.main;
        }
        return theme.palette.grey[500];
    };

    // Получаем уроки на выбранную дату
    const dayLessons = selectedDate ? getLessonsForDay(selectedDate) : [];

    // Блок со списком уроков
    const lessonsListBlock = (
        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
                flexWrap: 'wrap',
                gap: 1,
            }}>
                <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 600 }}>
                    📋 Уроки на {format(selectedDate, 'dd MMMM', { locale: ru })}
                </Typography>
                <Chip
                    label={`${dayLessons.length}`}
                    color="primary"
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                />
            </Box>

            {dayLessons.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 1.5, fontSize: '0.85rem' }}>
                    Нет уроков на эту дату
                </Typography>
            ) : (
                <Box sx={{
                    maxHeight: { xs: '300px', lg: 'calc(100vh - 280px)' },
                    overflowY: 'auto',
                }}>
                    {dayLessons.map((lesson) => (
                        <Paper
                            key={lesson.id}
                            elevation={0}
                            sx={{
                                p: 1.5,
                                mb: 0.75,
                                borderLeft: `3px solid ${getLessonColor(lesson)}`,
                                bgcolor: 'action.hover',
                                '&:hover': {
                                    bgcolor: 'action.selected',
                                },
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: { xs: 0.75, sm: 0 },
                            }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {getLessonTitle(lesson)}
                                        </Typography>
                                        {lesson.seriesId && (
                                            <Chip
                                                icon={<RepeatIcon fontSize="small" />}
                                                label="Серия"
                                                size="small"
                                                color="info"
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: '0.65rem' }}
                                            />
                                        )}
                                        {lesson.isFree && !isStudent(user) && (
                                            <Chip
                                                icon={<GiftIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                label="Бесплатно"
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: '0.65rem' }}
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                                        {formatLessonTimeRange(lesson)}  •  {lesson.topic}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 0.5,
                                    alignItems: 'center',
                                    alignSelf: { xs: 'flex-end', sm: 'center' },
                                    flexShrink: 0,
                                }}>
                                    <Chip
                                        label={lesson.studentId ? '🧑' : '👥'}
                                        size="small"
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                    {canEdit && (
                                        <>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenEdit(lesson)}
                                                disabled={loading}
                                                sx={{ p: 0.5 }}
                                            >
                                                <EditIcon sx={{ fontSize: '1rem' }} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleOpenDelete(lesson)}
                                                disabled={loading}
                                                sx={{ p: 0.5 }}
                                            >
                                                <DeleteIcon sx={{ fontSize: '1rem' }} />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            )}
        </Paper>
    );

    return (
        <Box>
            {/* Заголовок - адаптивный */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 },
                mb: 2.5
            }}>
                <Typography variant={isMobile ? 'h6' : 'h5'} component="h1" sx={{ fontWeight: 500 }}>
                    📅 Календарь
                </Typography>
                {canEdit && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                        disabled={loading}
                        fullWidth={isMobile}
                        size="small"
                    >
                        Добавить урок
                    </Button>
                )}
            </Box>

            {/* Управление календарем */}
            <Paper sx={{ p: { xs: 1.25, sm: 1.5 }, mb: 2.5 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1.5, sm: 0 },
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton onClick={handlePrevMonth} size="small">
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>

                        <Typography
                            variant="subtitle1"
                            sx={{
                                minWidth: { xs: '140px', sm: '180px' },
                                textAlign: 'center',
                                fontWeight: 600,
                                fontSize: '1rem',
                            }}
                        >
                            {format(currentDate, 'LLLL yyyy', { locale: ru })}
                        </Typography>

                        <IconButton onClick={handleNextMonth} size="small">
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<TodayIcon />}
                        onClick={handleToday}
                        size="small"
                        fullWidth={isMobile}
                    >
                        Сегодня
                    </Button>
                </Box>
            </Paper>

            {/* Двухколоночный layout на десктопе */}
            <Grid container spacing={2}>
                {/* Календарь — слева */}
                <Grid
                    size={{ xs: 12, lg: 8 }}
                    sx={{ order: { xs: 2, lg: 1 } }}
                >
                    <Paper sx={{ p: { xs: 0.75, sm: 1.5 }, overflow: 'hidden' }}>
                        {/* Дни недели */}
                        <Grid container spacing={0} sx={{ mb: 0.75 }}>
                            {WEEKDAYS.map((day) => (
                                <Grid key={day} size={{ xs: 12 / 7 }}>
                                    <Typography
                                        variant="body2"
                                        align="center"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            py: { xs: 0.4, sm: 0.6 },
                                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'action.hover',
                                            borderRadius: '4px 4px 0 0',
                                        }}
                                    >
                                        {day}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Дни месяца */}
                        <Grid container spacing={0}>
                            {calendarDays.map((day, index) => {
                                const dayLessonsList = getLessonsForDay(day);
                                const isCurrentMonth = isSameMonth(day, currentDate);
                                const isSelected = selectedDate && isSameDay(day, selectedDate);
                                const isTodayDate = isToday(day);
                                const isWeekend = getDay(day) === 0 || getDay(day) === 6;

                                const getCellBackground = () => {
                                    if (isSelected) return 'primary.light';
                                    if (!isCurrentMonth) return 'action.disabledBackground';
                                    if (isWeekend) return '#e8f5e9';
                                    if (isTodayDate) return '#fff9c4';
                                    return 'transparent';
                                };

                                const getCellBorderColor = () => {
                                    if (isSelected) return 'primary.main';
                                    if (isTodayDate) return '#fbc02d';
                                    if (!isCurrentMonth) return 'divider';
                                    if (isWeekend) return '#a5d6a7';
                                    return 'divider';
                                };

                                const getDayNumberColor = () => {
                                    if (!isCurrentMonth) return 'text.disabled';
                                    if (isSelected) return 'primary.dark';
                                    if (isTodayDate) return '#f57f17';
                                    if (isWeekend) return '#2e7d32';
                                    return 'text.primary';
                                };

                                return (
                                    <Grid
                                        key={index}
                                        size={{ xs: 12 / 7 }}
                                        sx={{
                                            minHeight: { xs: 60, sm: 85, md: 120 },
                                            p: { xs: 0.2, sm: 0.4 },
                                        }}
                                    >
                                        <Box
                                            onClick={() => handleDateClick(day)}
                                            sx={{
                                                height: '100%',
                                                p: { xs: 0.2, sm: 0.4 },
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                border: '1px solid',
                                                borderColor: getCellBorderColor(),
                                                backgroundColor: getCellBackground(),
                                                opacity: !isCurrentMonth ? 0.6 : 1,
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    backgroundColor: isSelected
                                                        ? 'primary.light'
                                                        : !isCurrentMonth
                                                            ? 'action.disabled'
                                                            : isWeekend
                                                                ? '#c8e6c9'
                                                                : isTodayDate
                                                                    ? '#fff59d'
                                                                    : theme.palette.action.hover,
                                                    boxShadow: theme.shadows[2],
                                                    opacity: 1,
                                                },
                                                display: 'flex',
                                                flexDirection: 'column',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {/* Число месяца */}
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mb: { xs: 0.2, sm: 0.4 },
                                            }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: isTodayDate ? 700 : 500,
                                                        fontSize: { xs: '0.65rem', sm: '0.72rem' },
                                                        color: getDayNumberColor(),
                                                        px: 0.4,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    {format(day, 'd')}
                                                </Typography>
                                                {isTodayDate && isCurrentMonth && !isMobile && (
                                                    <Chip
                                                        label="сегодня"
                                                        size="small"
                                                        sx={{
                                                            height: 14,
                                                            fontSize: '0.55rem',
                                                            bgcolor: '#fbc02d',
                                                            color: 'white',
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': { px: 0.4 },
                                                        }}
                                                    />
                                                )}
                                                {!isCurrentMonth && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontSize: { xs: '0.5rem', sm: '0.55rem' },
                                                            color: 'text.disabled',
                                                            pr: 0.4,
                                                            fontStyle: 'italic',
                                                        }}
                                                    >
                                                        {format(day, 'MMM', { locale: ru })}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Список уроков / счетчик */}
                                            <Box sx={{
                                                flex: 1,
                                                overflow: 'hidden',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.2,
                                                alignItems: isMobile ? 'center' : 'stretch',
                                                justifyContent: isMobile && dayLessonsList.length > 0 ? 'center' : 'flex-start',
                                            }}>
                                                {isMobile ? (
                                                    dayLessonsList.length > 0 && (
                                                        <Tooltip
                                                            title={
                                                                <Box>
                                                                    {dayLessonsList.map((lesson, idx) => (
                                                                        <Box key={idx} sx={{ mb: idx < dayLessonsList.length - 1 ? 0.5 : 0 }}>
                                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                {formatLessonTimeRange(lesson)}
                                                                            </Typography>
                                                                            <Typography variant="body2">
                                                                                {getLessonTitle(lesson)}
                                                                            </Typography>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            }
                                                            arrow
                                                            placement="top"
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: 22,
                                                                    height: 22,
                                                                    borderRadius: '50%',
                                                                    bgcolor: isSelected
                                                                        ? 'background.paper'
                                                                        : getLessonColor(dayLessonsList[0]) + '20',
                                                                    border: `2px solid ${getLessonColor(dayLessonsList[0])}`,
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.15s',
                                                                    '&:hover': {
                                                                        bgcolor: getLessonColor(dayLessonsList[0]) + '30',
                                                                        transform: 'scale(1.1)',
                                                                    },
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '0.6rem',
                                                                        fontWeight: 700,
                                                                        color: getLessonColor(dayLessonsList[0]),
                                                                    }}
                                                                >
                                                                    {dayLessonsList.length}
                                                                </Typography>
                                                            </Box>
                                                        </Tooltip>
                                                    )
                                                ) : (
                                                    <>
                                                        {dayLessonsList.slice(0, 4).map((lesson, idx) => (
                                                            <Tooltip
                                                                key={idx}
                                                                title={
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                            {formatLessonTimeRange(lesson)}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            {getLessonTitle(lesson)}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {lesson.topic}
                                                                        </Typography>
                                                                    </Box>
                                                                }
                                                                arrow
                                                                placement="top"
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        px: 0.4,
                                                                        py: 0.2,
                                                                        borderRadius: 0.5,
                                                                        bgcolor: isSelected
                                                                            ? 'background.paper'
                                                                            : getLessonColor(lesson) + (isCurrentMonth ? '15' : '08'),
                                                                        borderLeft: `2px solid ${getLessonColor(lesson)}`,
                                                                        fontSize: '0.58rem',
                                                                        lineHeight: 1.15,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        cursor: 'pointer',
                                                                        opacity: isCurrentMonth ? 1 : 0.7,
                                                                        transition: 'all 0.15s',
                                                                        '&:hover': {
                                                                            bgcolor: getLessonColor(lesson) + '30',
                                                                            opacity: 1,
                                                                        },
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            fontSize: '0.58rem',
                                                                            fontWeight: 600,
                                                                            color: getLessonColor(lesson),
                                                                        }}
                                                                    >
                                                                        {formatLessonTimeRange(lesson)}
                                                                    </Typography>
                                                                </Box>
                                                            </Tooltip>
                                                        ))}

                                                        {dayLessonsList.length > 4 && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontSize: '0.55rem',
                                                                    color: 'text.secondary',
                                                                    textAlign: 'center',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                +{dayLessonsList.length - 4} ещё
                                                            </Typography>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Paper>
                </Grid>

                {/* Уроки на выбранную дату — справа */}
                <Grid
                    size={{ xs: 12, lg: 4 }}
                    sx={{
                        order: { xs: 1, lg: 2 },
                    }}
                >
                    <Box sx={{
                        position: { lg: 'sticky' },
                        top: { lg: 80 },
                    }}>
                        {lessonsListBlock}
                    </Box>
                </Grid>
            </Grid>

            {/* Модальное окно создания/редактирования урока */}
            <ModalDialog
                open={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveClick}
                title={editingLessonId !== null ? 'Редактирование урока' : 'Добавление урока'}
                loading={formLoading}
                disabled={formLoading}
                maxWidth="md"
            >
                <LessonForm
                    ref={formRef}
                    initialData={editingLessonData}
                    selectedDate={selectedDate}
                    students={students}
                    groups={groups}
                    lessons={lessons}
                    editingLessonId={editingLessonId}
                    editingSeriesId={selectedLessonForAction?.seriesId}
                    isSeriesEdit={isSeriesEditMode}
                    onSubmit={handleSaveLesson}
                    loading={formLoading}
                />
            </ModalDialog>

            {/* Диалог подтверждения удаления одиночного урока */}
            <Dialog
                open={deleteConfirmDialogOpen}
                onClose={() => {
                    setDeleteConfirmDialogOpen(false);
                    setSelectedLessonForAction(null);
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeleteIcon color="error" />
                        Удаление урока
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Вы уверены, что хотите удалить урок?
                    </DialogContentText>

                    {selectedLessonForAction && (
                        <Box sx={{
                            mt: 2,
                            p: 1.5,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            borderLeft: `3px solid ${getLessonColor(selectedLessonForAction)}`,
                        }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {getLessonTitle(selectedLessonForAction)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {formatLessonTimeRange(selectedLessonForAction)} • {selectedLessonForAction.topic}
                            </Typography>
                        </Box>
                    )}

                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Это действие нельзя отменить.
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button
                        onClick={() => {
                            setDeleteConfirmDialogOpen(false);
                            setSelectedLessonForAction(null);
                        }}
                    >
                        Отмена
                    </Button>
                    <Button
                        onClick={handleConfirmDeleteSingle}
                        color="error"
                        variant="contained"
                        startIcon={<DeleteIcon />}
                    >
                        Удалить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог выбора области удаления */}
            <Dialog
                open={deleteScopeDialogOpen}
                onClose={() => setDeleteScopeDialogOpen(false)}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeleteIcon color="error" />
                        Удаление серии
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Этот урок — часть серии повторяющихся уроков.
                        Что вы хотите удалить?
                    </DialogContentText>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            • <strong>Только этот урок</strong> — будет удалён только выбранный урок
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            • <strong>Весь ряд (с текущего дня)</strong> — будут удалены все будущие уроки серии. Прошедшие уроки останутся для истории.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteScopeDialogOpen(false)}>Отмена</Button>
                    <Button onClick={handleDeleteSingle} color="warning">
                        Только этот
                    </Button>
                    <Button onClick={handleDeleteSeries} color="error" variant="contained">
                        Весь ряд
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Уведомления */}
            <Snackbar
                open={notification.open}
                autoHideDuration={5000}
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                    severity={notification.severity}
                    variant="filled"
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Calendar;